/**
 * `/demo?token=…` — public landing for admin-issued single-use demo links.
 *
 * Forwards the token to `POST /api/v0/demo/consume`, which sets the
 * ephemeral session cookie, then refreshes the session context and
 * navigates into `/my-documents?demo=1`.
 *
 * The consume endpoint is one-shot, so we guard against React 18 StrictMode
 * remounts with a ref — only the first effect run is allowed to call it.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from '../components/layout/AuthShell';
import { consumeDemoLink } from '../api/demo';
import { ApiCallError } from '../api/client';
import { useSession } from '../hooks/useSession';

type Status =
  | { kind: 'loading' }
  | { kind: 'error'; message: string };

export function DemoPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { loading, user, isDemo, refresh } = useSession();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    // Wait for the session probe to settle before deciding what to do.
    // If the user is already signed in as a normal account, consuming the
    // demo token here would replace their session cookie silently — bounce
    // them home with an explanation instead.
    if (loading) return;
    if (user && !isDemo) {
      startedRef.current = true;
      navigate('/', { replace: true, state: { alreadySignedInBanner: true } });
      return;
    }
    startedRef.current = true;

    if (!token) {
      setStatus({
        kind: 'error',
        message: 'This demo link is missing its token. Ask the admin who shared it for a fresh link.',
      });
      return;
    }

    void (async () => {
      try {
        const { redirectTo } = await consumeDemoLink({ token });
        await refresh();
        navigate(redirectTo, { replace: true });
      } catch (err) {
        const message =
          err instanceof ApiCallError
            ? err.apiError.message
            : 'Could not redeem this demo link. Please try again.';
        setStatus({ kind: 'error', message });
      }
    })();
  }, [token, loading, user, isDemo, refresh, navigate]);

  return (
    <AuthShell topRight={<Link to="/">Home</Link>}>
      <div className="auth-card">
        <div className="auth-eyebrow">
          <span className="num">DEMO</span> · Opening session
        </div>
        {status.kind === 'loading' ? (
          <>
            <h1 className="auth-title">
              Opening your <em>demo</em>…
            </h1>
            <p className="auth-sub">One moment while we set things up.</p>
          </>
        ) : (
          <>
            <h1 className="auth-title">
              Link <em>unavailable.</em>
            </h1>
            <p className="auth-sub">{status.message}</p>
            <p className="below-card">
              <Link to="/">Back to home</Link>
            </p>
          </>
        )}
      </div>
    </AuthShell>
  );
}
